import React, { useState, useMemo, useEffect } from "react";
import { makeStyles, fade } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import {
  Drawer,
  AppBar,
  Menu,
  MenuItem,
  Button,
  CssBaseline,
  Toolbar,
  Collapse,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
} from "@material-ui/core";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import {
  Apartment as ApartmentIcon,
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MonetizationOnIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  SwapHoriz as SwapHorizIcon,
  ExpandLess,
  ExpandMore,
  Business as BusinessIcon,
  People as PeopleIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
} from "@material-ui/icons";
import { Switch, Route, Link, Redirect } from "react-router-dom";
import { connect } from "redux-zero/react";
import axios from "axios";
import moment from "moment";
import store from "../store";
import actions from "../actions";
import { financialYears, periodLabel } from "../financialYears";
import Register from "./Register";
import Registered from "./Registered";
import SignIn from "./SignIn";
import Activate from "./Activate";
import ResetPassword from "./ResetPassword";
import ChangePassword from "./ChangePassword";
import SwitchSociety from "./SwitchSociety";
// import Backups from './backup/Backups';
import SocietyDetails from "./society/SocietyDetails";
import AccountGroups from "./accountgroup/AccountGroups";
import Accounts from "./account/Accounts";
import BillItems from "./billitem/BillItems";
import Members from "./member/Members";
import OpeningBalances from "./openingbalance/OpeningBalances";
import BillMasters from "./billmaster/BillMasters";
import Bills from "./bill/Bills";
import BillsView from "./bill/BillsView";
import Transactions, { transactionTabs } from "./transaction/Transactions";
import BankReconciliation from "./bankreconciliation/BankReconciliation";
import ReceiptsVouchersReport from "./report/ReceiptsVouchersReport";
import JournalVouchersReport from "./report/JournalVouchersReport";
import MemberBalanceReport from "./report/MemberBalanceReport";
import LedgerReport from "./report/LedgerReport";
import MultipleLedgersReport from "./report/MultipleLedgersReport";
import TrialBalanceReport from "./report/TrialBalanceReport";
import BalanceSheetReport from "./report/BalanceSheetReport";
import IncomeExpenditureReport from "./report/IncomeExpenditureReport";
import Businesses from "./business/Businesses";
import BusinessUsers from "./business/BusinessUsers";
import BusinessSocieties from "./business/BusinessSocieties";
import BillContributionsReport from "./report/BillContributionsReport";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    "& .MuiListItem-root.Mui-selected, & .MuiListItem-root.Mui-selected:hover": {
      backgroundColor: fade(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
    },
    "& .MuiListItem-root.Mui-selected .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
    "& .MuiListItem-root.Mui-selected .MuiListItemText-primary": {
      fontWeight: 500,
    },
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
    padding: theme.spacing(3),
  },
  toolbar: theme.mixins.toolbar,
  nested: {
    paddingLeft: theme.spacing(9),
  },
  societyName: {
    marginLeft: 80,
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  societySelect: {
    width: 400,
    maxWidth: "100%",
    "& .MuiInputBase-root": {
      fontSize: "1.25rem",
      fontWeight: 500,
    },
  },
  periodSelect: {
    marginLeft: 16,
  },
  menuSearch: {
    marginLeft: 16,
    width: 280,
    "& .MuiInputBase-input::placeholder": {
      color: "#fff",
      opacity: 0.7,
    },
  },
  appBarInput: {
    "& .MuiInputBase-root": {
      color: "#fff",
    },
    "& .MuiInputBase-input": {
      background: "transparent",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.5)",
    },
    "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#fff",
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiSelect-icon": {
      color: "#fff",
    },
  },
  link: {
    cursor: "pointer",
  },
  userMenuButton: {
    color: "#fff",
  },
}));

const filterSocieties = createFilterOptions({
  matchFrom: "any",
  ignoreCase: true,
  stringify: (option) => option.name,
});

const filterMenuItems = createFilterOptions({
  matchFrom: "any",
  ignoreCase: true,
  stringify: (option) => `${option.group || ""} ${option.label}`,
});

// Items of the left menu, for the global search in the top bar
function menuItems(selectedSociety) {
  const items = [{ label: "Switch Society", path: "/" }];
  if (selectedSociety === 0 && window.decodedJwt.userType === "Sharp") {
    items.push({ label: "Businesses", path: "/businesses" });
  }
  if (selectedSociety === 0 && window.decodedJwt.userType === "Business") {
    items.push({ label: "Users", path: "/businessusers" });
  }
  if (selectedSociety > 0) {
    items.push(
      { label: "Society Details", path: "/societydetails" },
      { label: "Accounts Groups", path: "/accountgroups", group: "Accounts" },
      { label: "Accounts", path: "/accounts", group: "Accounts" },
      { label: "Bill Items", path: "/billitems", group: "Accounts" },
      { label: "Members", path: "/members", group: "Accounts" },
      { label: "Opening Balances", path: "/openingbalances", group: "Accounts" },
      { label: "Bill Masters", path: "/billmasters", group: "Billing" },
      { label: "Bills", path: "/bills", group: "Billing" },
      { label: "Transactions", path: "/transactions" },
      ...transactionTabs.map((x) => ({
        label: x.label,
        path: "/transactions/" + x.path,
        group: "Transactions",
      })),
      { label: "Bank Reconciliation", path: "/bankreconciliation" },
      { label: "Receipts/Vouchers", path: "/reports/receiptsvouchers", group: "Reports" },
      { label: "Journal Vouchers", path: "/reports/journalvouchers", group: "Reports" },
      { label: "Member Outstanding", path: "/reports/memberbalance", group: "Reports" },
      { label: "Ledger", path: "/reports/ledger", group: "Reports" },
      { label: "Multiple Ledgers", path: "/reports/multipleledgers", group: "Reports" },
      { label: "Bill Contributions", path: "/reports/billcontributions", group: "Reports" },
      { label: "Trial Balance", path: "/reports/trialbalance", group: "Reports" },
      { label: "Balance Sheet", path: "/reports/balancesheet", group: "Reports" },
      { label: "Income Expenditure", path: "/reports/incomeexpenditure", group: "Reports" }
    );
  }
  return items;
}

function PrivateRoute({ children, ...rest }) {
  return (
    <Route
      {...rest}
      render={({ location }) =>
        store.getState().isAuthenticated ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/signin",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}

const mapToProps = ({
  isAuthenticated,
  selectedSociety,
  selectedSocietyName,
  currentPeriod,
}) => ({
  isAuthenticated,
  selectedSociety,
  selectedSocietyName,
  currentPeriod,
});

function Layout({
  setIsAuthenticated,
  setSelectedSociety,
  setSelectedSocietyName,
  setCurrentPeriod,
  setSocietyHeader,
  isAuthenticated,
  selectedSociety,
  selectedSocietyName,
  currentPeriod,
}) {
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();

  const [accountsOpen, setAccountsOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [societies, setSocieties] = useState([]);
  const visibleSocieties = useMemo(
    () => societies.filter((x) => !x.hidden && !x.deleted),
    [societies]
  );
  const societyHeader = useMemo(
    () => societies.find((x) => x.id === selectedSociety),
    [societies, selectedSociety]
  );
  const years = useMemo(
    () => (societyHeader ? financialYears(societyHeader) : []),
    [societyHeader]
  );
  const currentYearIndex = currentPeriod
    ? years.findIndex((x) =>
        moment(x.startDate).isSame(currentPeriod.startDate, "day")
      )
    : -1;
  const currentSociety = useMemo(
    () => ({ id: selectedSociety, name: selectedSocietyName }),
    [selectedSociety, selectedSocietyName]
  );

  const [menuSearchText, setMenuSearchText] = useState("");
  const searchableMenuItems = useMemo(
    () => (isAuthenticated ? menuItems(selectedSociety) : []),
    [isAuthenticated, selectedSociety]
  );

  // Focus the menu search with "/" (when not typing in a field) or Ctrl/Cmd + K.
  // Esc takes focus away from a field.
  useEffect(() => {
    function handleKeyDown(e) {
      const target = e.target;
      const isTyping =
        target.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
      if (e.key === "Escape" && isTyping) {
        target.blur();
        return;
      }
      const input = document.getElementById("menusearch");
      if (!input) return;
      const isSlash = e.key === "/" && !isTyping && !e.ctrlKey && !e.metaKey && !e.altKey;
      const isCtrlK = (e.key || "").toLowerCase() === "k" && (e.ctrlKey || e.metaKey);
      if (isSlash || isCtrlK) {
        e.preventDefault();
        input.focus();
        input.select();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // A menu item is selected when the current page is its path or below it,
  // e.g. /transactions/bankreceipts selects Transactions
  function isSelected(path) {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  }

  // Expand the group holding the current page so its selected item is visible
  useEffect(() => {
    const item = searchableMenuItems.find((x) => x.group && isSelected(x.path));
    if (!item) return;
    if (item.group === "Accounts") setAccountsOpen(true);
    if (item.group === "Billing") setBillingOpen(true);
    if (item.group === "Reports") setReportsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, searchableMenuItems]);

  function goToMenuItem(item) {
    if (!item) return;
    if (item.group === "Accounts") setAccountsOpen(true);
    if (item.group === "Billing") setBillingOpen(true);
    if (item.group === "Reports") setReportsOpen(true);
    setMenuSearchText("");
    history.push(item.path);
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function loadSocieties() {
    axios.get("/society/list").then((response) => {
      setSocieties(response.data);
    });
  }

  useEffect(() => {
    if (isAuthenticated && selectedSociety > 0) loadSocieties();
  }, [isAuthenticated, selectedSociety]);

  useEffect(() => {
    setSocietyHeader(societyHeader || null);
  }, [societyHeader, setSocietyHeader]);

  function switchSociety(society) {
    if (!society || society.id === selectedSociety) return;
    setSelectedSociety(society.id);
    setSelectedSocietyName(society.name);
    setCurrentPeriod({
      startDate: society.currentPeriodFrom,
      endDate: society.currentPeriodTo,
    });
  }

  function changePassword() {
    handleClose();
    history.push("/changepassword");
  }

  function signOut() {
    handleClose();
    localStorage.removeItem("accessToken");
    setIsAuthenticated(false);
    history.push("/signin");
  }

  return (
    <div className={classes.root}>
      <CssBaseline />
      {isAuthenticated && (
        <>
          <AppBar position="fixed" className={classes.appBar}>
            <Toolbar>
              <Typography variant="h6" noWrap>
                Sharp Society
              </Typography>
              <div className={classes.societyName}>
                {selectedSociety > 0 && (
                  <Autocomplete
                    className={`${classes.societySelect} ${classes.appBarInput}`}
                    size="small"
                    autoHighlight
                    disableClearable
                    blurOnSelect
                    selectOnFocus
                    value={currentSociety}
                    options={visibleSocieties}
                    filterOptions={filterSocieties}
                    getOptionLabel={(option) => option.name || ""}
                    getOptionSelected={(option, value) => option.id === value.id}
                    onOpen={loadSocieties}
                    onChange={(_, value) => switchSociety(value)}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                )}
                {selectedSociety > 0 && currentPeriod && (
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    className={`${classes.periodSelect} ${classes.appBarInput}`}
                    value={currentYearIndex >= 0 ? currentYearIndex : ""}
                    onChange={(e) => setCurrentPeriod(years[e.target.value])}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: () =>
                        `${moment(currentPeriod.startDate).format("DD/MM/YYYY")} - ${moment(currentPeriod.endDate).format("DD/MM/YYYY")}`,
                    }}
                  >
                    {years.map((year, index) => (
                      <MenuItem key={year.startDate} value={index}>
                        {periodLabel(year, societyHeader)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {selectedSociety > 0 && (
                  <Autocomplete
                    id="menusearch"
                    className={`${classes.menuSearch} ${classes.appBarInput}`}
                    size="small"
                    autoHighlight
                    openOnFocus
                    blurOnSelect
                    clearOnBlur
                    forcePopupIcon={false}
                    value={null}
                    inputValue={menuSearchText}
                    onInputChange={(_, value) => setMenuSearchText(value)}
                    options={searchableMenuItems}
                    filterOptions={filterMenuItems}
                    getOptionLabel={(option) => option.label || ""}
                    renderOption={(option) => (
                      <ListItemText
                        primary={option.label}
                        secondary={option.group}
                      />
                    )}
                    onChange={(_, value) => goToMenuItem(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Press / or Ctrl + K to search"
                      />
                    )}
                  />
                )}
              </div>
              <Button
                aria-controls="simple-menu"
                aria-haspopup="true"
                className={classes.userMenuButton}
                onClick={handleClick}
              >
                {window.decodedJwt.username}
              </Button>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={changePassword}>Change Password</MenuItem>
                <MenuItem onClick={signOut}>Sign out</MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>
          <Drawer
            className={classes.drawer}
            variant="permanent"
            classes={{
              paper: classes.drawerPaper,
            }}
            display="none"
          >
            <div className={classes.toolbar} />
            <List>
              <ListItem button component={Link} to="/" key="Home" selected={isSelected("/")}>
                <ListItemIcon>
                  <SwapHorizIcon />
                </ListItemIcon>
                <ListItemText primary="Switch Society" />
              </ListItem>
              {selectedSociety === 0 &&
                window.decodedJwt.userType === "Sharp" && (
                  <ListItem
                    button
                    component={Link}
                    to="/businesses"
                    selected={isSelected("/businesses")}
                    key="Business"
                  >
                    <ListItemIcon>
                      <BusinessIcon />
                    </ListItemIcon>
                    <ListItemText primary="Businesses" />
                  </ListItem>
                )}
              {selectedSociety === 0 &&
                window.decodedJwt.userType === "Business" && (
                  <ListItem
                    button
                    component={Link}
                    to="/businessusers"
                    selected={isSelected("/businessusers")}
                    key="Users"
                  >
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Users" />
                  </ListItem>
                )}
              {selectedSociety > 0 && (
                <>
                  <ListItem
                    button
                    component={Link}
                    to="/societydetails"
                    selected={isSelected("/societydetails")}
                    key="SocietyDetails"
                  >
                    <ListItemIcon>
                      <ApartmentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Society Details" />
                  </ListItem>
                  <ListItem
                    button
                    key="Accounts"
                    onClick={() => setAccountsOpen(!accountsOpen)}
                  >
                    <ListItemIcon>
                      <AccountBalanceIcon />
                    </ListItemIcon>
                    <ListItemText primary="Accounts" />
                    {accountsOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={accountsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/accountgroups"
                        selected={isSelected("/accountgroups")}
                        key="AccountGroups"
                      >
                        <ListItemText primary="Accounts Groups" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/accounts"
                        selected={isSelected("/accounts")}
                        key="Accounts"
                      >
                        <ListItemText primary="Accounts" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/billitems"
                        selected={isSelected("/billitems")}
                        key="BillItems"
                      >
                        <ListItemText primary="Bill Items" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/members"
                        selected={isSelected("/members")}
                        key="Members"
                      >
                        <ListItemText primary="Members" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/openingbalances"
                        selected={isSelected("/openingbalances")}
                        key="OpeningBalances"
                      >
                        <ListItemText primary="Opening Balances" />
                      </ListItem>
                    </List>
                  </Collapse>
                  <ListItem
                    button
                    key="Billing"
                    onClick={() => setBillingOpen(!billingOpen)}
                  >
                    <ListItemIcon>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText primary="Billing" />
                    {billingOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={billingOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/billmasters"
                        selected={isSelected("/billmasters")}
                        key="BillMasters"
                      >
                        <ListItemText primary="Bill Masters" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/bills"
                        selected={isSelected("/bills")}
                        key="Bills"
                      >
                        <ListItemText primary="Bills" />
                      </ListItem>
                    </List>
                  </Collapse>
                  <ListItem
                    button
                    component={Link}
                    to="/transactions"
                    selected={isSelected("/transactions")}
                    key="Transactions"
                  >
                    <ListItemIcon>
                      <MonetizationOnIcon />
                    </ListItemIcon>
                    <ListItemText primary="Transactions" />
                  </ListItem>
                  <ListItem
                    button
                    component={Link}
                    to="/bankreconciliation"
                    selected={isSelected("/bankreconciliation")}
                    key="BankReconciliation"
                  >
                    <ListItemIcon>
                      <PlaylistAddCheckIcon />
                    </ListItemIcon>
                    <ListItemText primary="Bank Reconciliation" />
                  </ListItem>
                  <ListItem
                    button
                    key="Reports"
                    onClick={() => setReportsOpen(!reportsOpen)}
                  >
                    <ListItemIcon>
                      <TrendingUpIcon />
                    </ListItemIcon>
                    <ListItemText primary="Reports" />
                    {reportsOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/receiptsvouchers"
                        selected={isSelected("/reports/receiptsvouchers")}
                        key="ReceiptsVouchers"
                      >
                        <ListItemText primary="Receipts/Vouchers" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/journalvouchers"
                        selected={isSelected("/reports/journalvouchers")}
                        key="JournalVouchers"
                      >
                        <ListItemText primary="Journal Vouchers" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/memberbalance"
                        selected={isSelected("/reports/memberbalance")}
                        key="MemberBalance"
                      >
                        <ListItemText primary="Member Outstanding" />
                      </ListItem>
                      <Divider />
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/ledger"
                        selected={isSelected("/reports/ledger")}
                        key="Ledger"
                      >
                        <ListItemText primary="Ledger" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/multipleledgers"
                        selected={isSelected("/reports/multipleledgers")}
                        key="MultipleLedgers"
                      >
                        <ListItemText primary="Multiple Ledgers" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/billcontributions"
                        selected={isSelected("/reports/billcontributions")}
                        key="BillContributions"
                      >
                        <ListItemText primary="Bill Contributions" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/trialbalance"
                        selected={isSelected("/reports/trialbalance")}
                        key="TrialBalance"
                      >
                        <ListItemText primary="Trial Balance" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/balancesheet"
                        selected={isSelected("/reports/balancesheet")}
                        key="BalanceSheet"
                      >
                        <ListItemText primary="Balance Sheet" />
                      </ListItem>
                      <ListItem
                        button
                        component={Link}
                        className={classes.nested}
                        to="/reports/incomeexpenditure"
                        selected={isSelected("/reports/incomeexpenditure")}
                        key="IncomeExpenditure"
                      >
                        <ListItemText primary="Income Expenditure" />
                      </ListItem>
                    </List>
                  </Collapse>
                </>
              )}
            </List>
            <Divider />
          </Drawer>
        </>
      )}
      <main className={classes.content}>
        <div className={classes.toolbar} />
        <Switch
          key={`${selectedSociety}-${currentPeriod && currentPeriod.startDate}`}
        >
          <Route path="/register" component={Register} />
          <Route path="/registered" component={Registered} />
          <Route path="/signin" component={SignIn} />
          <Route path="/activate" component={Activate} />
          <Route path="/resetpassword" component={ResetPassword} />
          <Route path="/changepassword" component={ChangePassword} />
          <PrivateRoute path="/" exact>
            <SwitchSociety />
          </PrivateRoute>
          {/* <PrivateRoute path="/backups"><Backups /></PrivateRoute> */}
          <PrivateRoute path="/societydetails">
            <SocietyDetails />
          </PrivateRoute>
          <PrivateRoute path="/accountgroups">
            <AccountGroups />
          </PrivateRoute>
          <PrivateRoute path="/accounts">
            <Accounts />
          </PrivateRoute>
          <PrivateRoute path="/billitems">
            <BillItems />
          </PrivateRoute>
          <PrivateRoute path="/members">
            <Members />
          </PrivateRoute>
          <PrivateRoute path="/openingbalances">
            <OpeningBalances />
          </PrivateRoute>
          <PrivateRoute path="/billmasters">
            <BillMasters />
          </PrivateRoute>
          <PrivateRoute path="/bills" exact>
            <Bills />
          </PrivateRoute>
          <PrivateRoute path="/bills/:billdate" exact>
            <BillsView />
          </PrivateRoute>
          <PrivateRoute path="/transactions/:tab?">
            <Transactions />
          </PrivateRoute>
          <PrivateRoute path="/bankreconciliation">
            <BankReconciliation />
          </PrivateRoute>
          <PrivateRoute path="/reports/receiptsvouchers">
            <ReceiptsVouchersReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/journalvouchers">
            <JournalVouchersReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/memberbalance">
            <MemberBalanceReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/ledger">
            <LedgerReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/multipleledgers">
            <MultipleLedgersReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/billcontributions">
            <BillContributionsReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/trialbalance">
            <TrialBalanceReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/balancesheet">
            <BalanceSheetReport />
          </PrivateRoute>
          <PrivateRoute path="/reports/incomeexpenditure">
            <IncomeExpenditureReport />
          </PrivateRoute>
          <PrivateRoute path="/businessusers">
            <BusinessUsers />
          </PrivateRoute>
          <PrivateRoute path="/businesses" exact>
            <Businesses />
          </PrivateRoute>
          <PrivateRoute path="/businesses/:id/users" exact>
            <BusinessUsers />
          </PrivateRoute>
          <PrivateRoute path="/businesses/:id/societies" exact>
            <BusinessSocieties />
          </PrivateRoute>
        </Switch>
      </main>
    </div>
  );
}

export default connect(mapToProps, actions)(Layout);
